import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Minus, Zap, X } from "lucide-react";
import { api, formatHours, today } from "@/lib/api";
import { toast } from "sonner";
import CapacityCalendar from "@/components/CapacityCalendar";

const STEP = 0.25;

/**
 * Unified dialog for creating or editing a quick repair.
 * Props:
 *   - initial: existing quick repair object -> edit mode
 *   - open, onOpenChange: controlled (used in both create-controlled and edit)
 *   - onCreated: (repair) => void  (create success)
 *   - onSaved: (repair) => void    (edit success)
 *   - initialDate: pre-fill date in create mode
 *   - trigger: optional trigger for uncontrolled create mode
 */
export default function AddQuickDialog({ initial = null, open: controlledOpen, onOpenChange, onCreated, onSaved, initialDate = null, trigger }) {
  const isEdit = !!initial;
  const isControlled = controlledOpen !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = isControlled ? controlledOpen : openState;
  const setOpen = isControlled ? (onOpenChange || (() => {})) : setOpenState;

  const [hours, setHours] = useState(0.5);
  const [description, setDescription] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [lookedUpPlate, setLookedUpPlate] = useState("");

  const lookupPlate = async () => {
    if (isEdit) return;
    const plate = licensePlate.trim().toUpperCase();
    if (!plate || plate.length < 4 || plate === lookedUpPlate) return;
    setLookedUpPlate(plate);
    try {
      const { data } = await api.get(`/vehicles/${encodeURIComponent(plate)}/history`);
      const v = data?.vehicle;
      if (!v || !data.total_count) return;
      let filled = 0;
      if (v.customer_name && !customerName.trim()) { setCustomerName(v.customer_name); filled++; }
      if (v.phone && !phone.trim())               { setPhone(v.phone);               filled++; }
      if (v.brand && !brand.trim())               { setBrand(v.brand);               filled++; }
      if (v.model && !model.trim())               { setModel(v.model);               filled++; }
      if (filled > 0) toast.success(`Cliente autocompletado desde ${plate}`, { duration: 2500 });
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (open && initial) {
      setHours(initial.estimated_hours || initial.hours || 0.5);
      setDescription(initial.description || "");
      setCustomerName(initial.customer_name || "");
      setLicensePlate(initial.license_plate || "");
      setBrand(initial.brand || "");
      setModel(initial.model || "");
      setPhone(initial.phone || "");
      setNotes(initial.notes || "");
      setDate(initial.date || today());
    } else if (open && !initial) {
      setHours(0.5);
      setDescription("");
      setCustomerName("");
      setLicensePlate("");
      setBrand("");
      setModel("");
      setPhone("");
      setNotes("");
      setDate(initialDate || today());
    }
  }, [open, initial, initialDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) { toast.error("Describe la tarea"); return; }
    setSubmitting(true);
    try {
      const body = {
        description: description.trim(),
        customer_name: customerName.trim(),
        license_plate: licensePlate.trim().toUpperCase(),
        brand: brand.trim(),
        model: model.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
        estimated_hours: hours,
        date,
      };
      const res = isEdit
        ? await api.put(`/quick-repairs/${initial.id}`, body)
        : await api.post("/quick-repairs", body);
      toast.success(isEdit ? "Tarea actualizada" : "Tarea rápida añadida");
      setOpen(false);
      onCreated && onCreated(res.data);
      onSaved && onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Error al actualizar" : "Error al añadir"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && !isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button data-testid="add-quick-button" className="bg-amber-500 hover:bg-amber-600 text-white h-12 px-6">
              <Zap className="w-4 h-4 mr-2" />
              Nueva Rápida
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0 gap-0 [&>button.absolute]:hidden" data-testid={isEdit ? "edit-quick-dialog" : "add-quick-dialog"}>
        <button
          type="button"
          onClick={() => setOpen(false)}
          data-testid="exit-quick-dialog-btn"
          className="sticky top-0 z-10 w-full flex items-center justify-center gap-2 h-12 bg-slate-900 hover:bg-slate-800 text-white font-display font-bold text-base transition-colors rounded-t-lg"
        >
          <X className="w-5 h-5" />
          Salir
        </button>
        <div className="p-6 space-y-4">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {isEdit ? "Editar Tarea Rápida" : "Nueva Cita · Mecánica Rápida"}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Cambia los datos. Si modificas la fecha o las horas se validará la capacidad."
                : "Describe la tarea y asigna el tiempo estimado. Solo cabrá si el día tiene hueco."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="qdesc">Descripción de la tarea *</Label>
              <Textarea
                id="qdesc"
                data-testid="quick-description-input"
                placeholder="Ej: Cambio de bombilla, grúa con coche..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label>Tiempo estimado</Label>
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md p-3">
                <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => setHours((h) => Math.max(STEP, +(h - STEP).toFixed(2)))} data-testid="quick-decrease-time">
                  <Minus className="w-5 h-5" />
                </Button>
                <div className="flex-1 text-center" data-testid="quick-hours-display">
                  <div className="text-3xl font-display font-bold text-slate-900">{formatHours(hours)}</div>
                  <div className="text-xs text-slate-500">incrementos de 15 min</div>
                </div>
                <Button type="button" variant="outline" size="icon" className="h-12 w-12 shrink-0" onClick={() => setHours((h) => +(h + STEP).toFixed(2))} data-testid="quick-increase-time">
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="qcustomer">Nombre del cliente</Label>
                <Input id="qcustomer" data-testid="quick-customer-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Nombre" className="h-12" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qphone">Teléfono</Label>
                <Input id="qphone" data-testid="quick-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="600 000 000" className="h-12" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="qplate">Matrícula</Label>
                <Input id="qplate" data-testid="quick-plate-input" value={licensePlate} onChange={(e) => setLicensePlate(e.target.value)} onBlur={lookupPlate} placeholder="0000ABC" className="h-12 uppercase" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qbrand">Marca</Label>
                <Input id="qbrand" data-testid="quick-brand-input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Seat" className="h-12" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="qmodel">Modelo</Label>
                <Input id="qmodel" data-testid="quick-model-input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="León" className="h-12" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="qnotes">Notas / comentarios</Label>
              <Textarea
                id="qnotes"
                data-testid="quick-notes-input"
                placeholder="Anotaciones sobre esta tarea..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="min-h-[70px]"
              />
            </div>

            <div className="space-y-2">
              <Label>{isEdit ? "Fecha (si cambias, se valida hueco)" : "Fecha de la cita"}</Label>
              <CapacityCalendar
                value={date}
                onChange={setDate}
                capacityEndpoint="/quick-capacity"
                neededHours={hours}
                testid={isEdit ? "edit-quick-date" : "quick-date"}
              />
              <p className="text-xs text-slate-500">
                Los días en rojo están llenos, son festivos o no laborables.
              </p>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-12 w-full sm:w-auto">Cancelar</Button>
              <Button type="submit" disabled={submitting} className="h-12 w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-base" data-testid="save-quick-button">
                <Zap className="w-4 h-4 mr-2" />
                {submitting ? (isEdit ? "Guardando..." : "Añadiendo...") : (isEdit ? "Guardar cambios" : "Añadir tarea rápida")}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
